import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LeaderboardEntry, SalespersonMetrics } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface TalkListenByRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface RepTalkListen {
  name: string;
  talkPct: number;
  listenPct: number;
}

const TalkListenByRepChart: React.FC<TalkListenByRepChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [repData, setRepData] = useState<RepTalkListen[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const leaderboard = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          timeRange,
          'total_conversations',
          15
        );
        const entries = leaderboard.leaderboard || [];

        if (entries.length === 0) {
          setRepData([]);
          return;
        }

        const metricsResults = await Promise.allSettled(
          entries.map((entry) => analyticsService.getSalespersonMetrics(entry.salesperson_id))
        );

        const combined: RepTalkListen[] = [];
        metricsResults.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            const metrics = result.value;
            combined.push({
              name: entries[idx].salesperson_name,
              talkPct: metrics.average_talk_percentage || 0,
              listenPct: metrics.average_listen_percentage || 0,
            });
          }
        });

        setRepData(combined);
      } catch (err: any) {
        console.error('[TalkListenByRepChart] Error fetching data:', err);
        setError(err.message || 'Failed to load talk/listen data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (repData.length === 0) return {};

    const names = repData.map((r) => r.name);
    const talkValues = repData.map((r) => r.talkPct);
    const listenValues = repData.map((r) => r.listenPct);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const name = params[0]?.name || '';
          let html = `<b>${name}</b><br/>`;
          params.forEach((p: any) => {
            html += `${p.marker} ${p.seriesName}: ${p.value?.toFixed(1)}%<br/>`;
          });
          return html;
        },
      },
      legend: {
        data: ['Talk %', 'Listen %'],
        bottom: 0,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 11 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: 100,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
          formatter: '{value}%',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 80,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          name: 'Talk %',
          type: 'bar',
          stack: 'total',
          data: talkValues,
          itemStyle: {
            color: CHART_COLORS.primary,
            borderRadius: [0, 0, 0, 0],
          },
          barWidth: '50%',
        },
        {
          name: 'Listen %',
          type: 'bar',
          stack: 'total',
          data: listenValues,
          itemStyle: {
            color: CHART_COLORS.secondary,
            borderRadius: [0, 4, 4, 0],
          },
          barWidth: '50%',
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Talk vs Listen Ratio by Rep"
      subtitle="Salesperson talk/listen percentage breakdown"
      isLoading={isLoading}
      error={error}
    >
      {repData.length === 0 && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, repData.length * 35) + 'px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default TalkListenByRepChart;
